import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AuthenticationService } from '../../../backend/api';
import { AuthenticationContextDatasourceService } from '../../../backend/datasource/authentication-context-datasource.service';
import { UserChoiceData } from '../../../backend/model/user-choice-data';
import { UserChoiceTableDataItem } from '../../model/user-choice-table-data-item';
import { IDInputModule } from '@inera/ids-angular/components/form/input';
import { FilterUserChoicesPipe, UserChoiceSortPipe } from '../../pipe';
import { NgFor, NgTemplateOutlet } from '@angular/common';
import { IDCardModule } from '@inera/ids-angular/components/card';
import { IDButtonModule } from '@inera/ids-angular/components/button';
import { LoadingSpinnerComponent } from '../../../shared/component/loading-spinner/loading-spinner.component';
import { ThemeHandlerService } from '../../service/theme-handler.service';
import { ThemeType } from '../../../backend/model/theme-type';

export enum TableType {
  ASSIGNMENT,
  COMMISSION,
  ORGANIZATION
}

@Component({
  selector: 'idp-user-choice',
  imports: [
    ReactiveFormsModule,
    IDInputModule,
    FilterUserChoicesPipe,
    UserChoiceSortPipe,
    NgFor,
    IDCardModule,
    IDButtonModule,
    LoadingSpinnerComponent,
    NgTemplateOutlet
  ],
  templateUrl: './user-choice.component.html',
  styleUrls: ['./user-choice.component.scss']
})
export class UserChoiceComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private authenticationService = inject(AuthenticationService);
  private authenticationContextDatasource = inject(AuthenticationContextDatasourceService);
  private fb = inject(FormBuilder);
  private themeService = inject(ThemeHandlerService);

  public loading = true;
  public sortReverse = false;

  public tableSortColumn = 'employeeHsaId';
  public filter = '';
  public mainTheme = '';

  public themeType = ThemeType;

  public clientInfo;
  public filterForm: FormGroup;

  public userChoices: UserChoiceTableDataItem[];

  public TableType = TableType;
  public tableType: TableType = TableType.ASSIGNMENT;

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.authenticationContextDatasource.setContextData(params);

      this.themeService.getTheme().subscribe(theme => {
        this.mainTheme = theme.value.value.theme.mainTheme;
      });

      this.clientInfo = this.authenticationContextDatasource.getClientInfo();
      this.getUserChoices();
    });

    this.createForm();
  }

  private createForm(): void {
    this.filterForm = this.fb.group({
      filter: ''
    });
  }

  private getUserChoices(): void {
    this.loading = true;

    this.authenticationService
      .getUserChoices(
        this.authenticationContextDatasource.getIdp(),
        this.authenticationContextDatasource.getSessionId()
      )
      .subscribe(userChoiceData => {
        this.setTableSettings(userChoiceData);

        this.buildTableData(userChoiceData);

        if (this.userChoices.length == 1) {
          this.makeUserChoice(this.userChoices[0]);
        } else {
          this.loading = false;
        }
      });
  }

  private setTableSettings(userChoiceData: UserChoiceData) {
    if (userChoiceData.commissionChoices?.length > 0) {
      this.tableType = TableType.COMMISSION;
      this.tableSortColumn = 'commissionName';
    } else if (userChoiceData.organizationChoices?.length > 0) {
      this.tableType = TableType.ORGANIZATION;
      this.tableSortColumn = 'organizationHsaId';
    } else {
      this.tableType = TableType.ASSIGNMENT;
      this.tableSortColumn = 'employeeHsaId';
    }
  }

  private buildTableData(userChoiceData: UserChoiceData): void {
    this.userChoices = [];

    if (userChoiceData.assignmentChoices?.length > 0) {
      this.userChoices.push(
        ...userChoiceData.assignmentChoices.map(it => {
          return {
            employeeHsaId: it.employeeHsaId
          };
        })
      );
    }

    if (userChoiceData.commissionChoices?.length > 0) {
      this.userChoices.push(
        ...userChoiceData.commissionChoices.map(it => {
          return {
            employeeHsaId: it.employeeHsaId,
            commissionHsaId: it.commissionHsaId,
            commissionName: it.commissionName,
            commissionPurpose: it.commissionPurpose,
            healthCareProfessionalLicence: it.healthCareProfessionalLicence,
            healthCareProviderName: it.healthCareProviderName,
            healthCareUnitName: it.healthCareUnitName
          };
        })
      );
    } else if (userChoiceData.organizationChoices?.length > 0) {
      this.userChoices.push(
        ...userChoiceData.organizationChoices.map(it => {
          return {
            employeeHsaId: it.employeeHsaId,
            organizationHsaId: it.organizationHsaId,
            organizationIdentifier: it.organizationIdentifier,
            organizationName: it.organizationName
          };
        })
      );
    }
  }

  public makeUserChoice(userChoice: UserChoiceTableDataItem): void {
    this.loading = true;

    this.authenticationService.doUserChoice(
      this.authenticationContextDatasource.getIdp(),
      this.authenticationContextDatasource.getSessionId(),
      userChoice.employeeHsaId,
      userChoice.commissionHsaId,
      userChoice.organizationHsaId
    );
  }

  public setSortCol(columnName: string): void {
    if (this.tableSortColumn === columnName) {
      this.sortReverse = !this.sortReverse;
    } else {
      this.tableSortColumn = columnName;
      this.sortReverse = false;
    }
  }

  public getAuthTypeText(): string {
    return this.authenticationContextDatasource.getIsSignature() ? 'Jag signerar hos' : 'Jag legitimerar mig hos';
  }

  public getTypeText(): string {
    switch (this.tableType) {
      case TableType.COMMISSION:
        return 'Välj ett medarbetaruppdrag';
      case TableType.ORGANIZATION:
        return 'Välj en organisation';
      case TableType.ASSIGNMENT:
      default:
        return 'Välj ett tjänste-id';
    }
  }
}
